import { Button, Dialog, Flex, Input, P } from '@dnb/eufemia';
import { email_medium as EmailMediumIcon } from '@dnb/eufemia/icons';
import { useEufemiaForm } from '@portals/shared-frontend/hooks/useEufemiaForm';
import { type JSX, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { requestEditSubscriptionsEmail } from '@/api/subscriptions';
import Container from '@/components/Container';
import ModalPage from '@/components/ModalPage';
import useReturnTo from '@/hooks/useReturnTo';
import { useUser } from '@/hooks/useUser';

const unsubscribeFormSchema = z.object({
  email: z.string().email(),
});

export default function Unsubscribe(): JSX.Element {
  const { user, isLoggedIn } = useUser();
  const {
    submitting,
    register,
    handleSubmit,
    controller: { formError, getValue },
  } = useEufemiaForm(unsubscribeFormSchema, {
    email: isLoggedIn ? user?.email : '',
  });

  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [returnTo, , clearReturnTo] = useReturnTo();

  const onSubmit = handleSubmit(
    useCallback(async (data) => {
      await requestEditSubscriptionsEmail(data.email);
      setOpen(true);
    }, []),
  );

  const formEmailValue = getValue('email') as string | undefined;

  return (
    <ModalPage
      onClose={() => {
        navigate(returnTo ?? '/');
        clearReturnTo();
      }}
      subtitle="Unsubscribe"
      title="Subscriptions"
    >
      <Container size="small">
        <form onSubmit={onSubmit}>
          <Flex.Horizontal align="baseline">
            <P>
              Enter the email that you subscribed with. We&apos;ll send you an email containing a link you can use to
              unsubscribe.
            </P>
            <Input
              icon={EmailMediumIcon}
              placeholder="Email address"
              size="large"
              stretch
              top="small"
              {...register.input('email')}
            />
          </Flex.Horizontal>
          <Flex.Horizontal align="baseline">
            <Button disabled={submitting} size="large" status={formError} top="small" type="submit">
              Send me a link
            </Button>
          </Flex.Horizontal>
        </form>
      </Container>
      <Dialog
        confirmText="Got it!"
        description={
          <P>
            We&apos;ve sent a confirmation email to {formEmailValue}. If you didn&apos;t receive it, check your spam
            filter.
          </P>
        }
        hideDecline
        onConfirm={() => {
          navigate(returnTo ?? '/');
          clearReturnTo();
        }}
        open={open}
        title="Success! An email is on its way"
        variant="confirmation"
      />
    </ModalPage>
  );
}
